/*
Pre-persist image pipeline: decode → resize → JPEG re-encode.

Two reasons we run this on every captured photo before persisting:
  1. **Privacy** — iOS / Android cameras stamp the JPEG with EXIF
     including GPS, device make/model, software version. Drawing the
     decoded bitmap to a fresh canvas drops every EXIF tag (canvas
     export starts from a blank metadata slate).
  2. **Quota** — a raw 12 MP iPhone JPEG is ~3-5 MB; HEIC even more
     after the implicit transcode `<input type="file">` performs.
     Re-encoding at long-edge 1600 px / quality 0.85 brings the
     typical capture down to ~300-700 KB, so 1 GB of IDB photos
     ≈ 1500-3000 captures instead of ~250.

Mirrors the Android `compressCapture` in CaptureLauncher.kt.

Implementation note for iOS Safari: `createImageBitmap(blob)` is
unreliable with HEIC inputs in standalone PWA mode (silently fails
on some iOS versions). Decoding via `<img>` + an object URL is
universal — modern browsers auto-apply EXIF orientation when an
`<img>` element renders, so the canvas draw lands the bitmap in its
visual-up orientation without our help.
*/

const kTargetLongEdgePx: number = 1600  // matches Android Phase 12 (TARGET_LONG_EDGE)
const kJpegQuality: number = 0.85       // matches Android Phase 12 (JPEG_QUALITY = 85)

/*
Decode the input blob, downscale so the longest side fits within
kTargetLongEdgePx (no upscale — small inputs pass through at native
size), and re-encode as JPEG. Returns the new blob; the original
input is left untouched.

Fails safely: any decode / canvas / encode error rejects with a
descriptive message. The capture flow surfaces these as a generic
"erreur de traitement" toast rather than corrupting the IDB store.
*/
export async function reencodeForStorage(inBlob: Blob): Promise<Blob>
{
	const vUrl = URL.createObjectURL(inBlob)  // ephemeral object URL fed to the Image element
	try
	{
		const vImg = await loadImage(vUrl)
		const vSrcW = vImg.naturalWidth
		const vSrcH = vImg.naturalHeight
		if (vSrcW <= 0 || vSrcH <= 0) throw new Error('Image has zero dimensions')

		const vLong = Math.max(vSrcW, vSrcH)
		const vScale = vLong > kTargetLongEdgePx ? kTargetLongEdgePx / vLong : 1
		const vDstW = Math.max(1, Math.round(vSrcW * vScale))
		const vDstH = Math.max(1, Math.round(vSrcH * vScale))

		const vCanvas = document.createElement('canvas')  // off-DOM canvas, used purely as an encoding surface
		vCanvas.width = vDstW
		vCanvas.height = vDstH
		const vCtx = vCanvas.getContext('2d')
		if (!vCtx) throw new Error('2D context unavailable')
		vCtx.drawImage(vImg, 0, 0, vDstW, vDstH)

		const vOut = await canvasToBlob(vCanvas, 'image/jpeg', kJpegQuality)
		if (!vOut) throw new Error('Canvas toBlob returned null')
		return vOut
	}
	finally
	{
		URL.revokeObjectURL(vUrl)
	}
}

/*
`<img>` based decoder. Resolves once the native pipeline has fully
loaded the bitmap (works for JPEG, PNG, HEIC since iOS 14, WebP).
*/
function loadImage(inUrl: string): Promise<HTMLImageElement>
{
	return new Promise<HTMLImageElement>((inResolve, inReject) => {
		const vImg = new Image()  // ephemeral decoder, never inserted into the DOM
		vImg.onload = () => inResolve(vImg)
		vImg.onerror = () => inReject(new Error('Image decode failed'))
		vImg.src = inUrl
	})
}

/*
Promise wrapper around HTMLCanvasElement.toBlob — the API itself is
callback-based. iOS Safari resolves it on the next microtask.
*/
function canvasToBlob(inCanvas: HTMLCanvasElement, inMime: string, inQuality: number): Promise<Blob | null>
{
	return new Promise<Blob | null>((inResolve) => {
		inCanvas.toBlob((inBlob) => inResolve(inBlob), inMime, inQuality)
	})
}
